<template>
  <div
    ref="root"
    class="capacity-selector"
    :class="{ 'capacity-selector--flipped': flipped }"
    data-testid="capacity-selector"
  >
    <span class="capacity-selector__tail" data-testid="capacity-selector-tail" aria-hidden="true"></span>
    <div class="capacity-selector__body" role="group" aria-label="Capacity">
      <div class="capacity-selector__header">Capacity</div>
      <v-radio-group
        hide-details
        data-name="radiogroup"
        class="capacity-selector__options mt-0 pt-0"
        :value="value"
        @change="onChange"
      >
        <v-radio
          v-for="option in OPTIONS"
          :key="option.value"
          :data-name="option.dataName"
          :value="option.value"
          :ripple="false"
          class="capacity-selector__option"
        >
          <template #label>
            <span class="capacity-selector__option-content">
              <CapacityIconWithoutSelector :value="option.value" occupied="not" />
              <span class="capacity-selector__label">{{ option.label }}</span>
            </span>
          </template>
        </v-radio>
      </v-radio-group>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import CapacityIconWithoutSelector from "@components/organizations/positions/CapacityIconWithoutSelector.vue";

const OPTIONS = [
  { value: 1, label: "1 FTE", dataName: "fte1radio" },
  { value: 0.5, label: "0.5 FTE", dataName: "fte05radio" },
];

const props = defineProps({
  value: {
    type: Number,
    default: 1,
  },
  // True while the selector is open, so the checked radio can take focus.
  active: {
    type: Boolean,
    default: false,
  },
  // True when the selector opens upwards: the tail moves to the bottom edge and points down.
  flipped: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["input"]);
const root = ref(null);

const onChange = (capacity) => {
  if (capacity !== props.value) emit("input", capacity);
};

watch(
  () => props.active,
  (isActive) => {
    if (!isActive) return;
    // The menu is displayed a tick after `active` flips; a hidden radio cannot take focus yet.
    setTimeout(() => root.value?.querySelector("input[type='radio']:checked")?.focus(), 0);
  }
);
</script>

<style scoped>
.capacity-selector {
  position: relative;
  box-sizing: border-box;
  width: 200px;
  padding-top: 8px;
  font-family: Inter, sans-serif;
}
.capacity-selector--flipped {
  padding-top: 0;
  padding-bottom: 8px;
  margin-bottom: -8px;
}
.capacity-selector__tail {
  position: absolute;
  top: 0;
  left: 16px;
  z-index: 1;
  width: 16px;
  height: 9px;
  background: #ebedf0;
  clip-path: polygon(50% 0, 0 100%, 100% 100%);
}
.capacity-selector__tail::after {
  content: "";
  position: absolute;
  top: 1px;
  left: 0;
  width: 100%;
  height: 100%;
  background: #ffffff;
  clip-path: polygon(50% 0, 0 100%, 100% 100%);
}
.capacity-selector--flipped .capacity-selector__tail {
  top: auto;
  bottom: 0;
  transform: rotate(180deg);
}
.capacity-selector__body {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #ebedf0;
  border-radius: 8px;
  box-shadow: 0 4px 8px -2px rgba(16, 24, 40, 0.1), 0 2px 4px -2px rgba(16, 24, 40, 0.06);
}
.capacity-selector__header {
  padding: 4px 10px 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 16.94px;
  color: #101828;
}
.capacity-selector__options {
  margin: 0;
  padding: 0;
}
.capacity-selector__options ::v-deep .v-input--radio-group__input {
  gap: 4px;
}
.capacity-selector__options .capacity-selector__option:not(:last-child):not(:only-child) {
  margin-bottom: 0;
}
.capacity-selector__option {
  box-sizing: border-box;
  height: 40px;
  margin: 0;
  padding: 8px 10px;
  border-radius: 6px;
}
.capacity-selector__option:focus-within {
  outline: 2px solid #7f56d9;
  outline-offset: -2px;
}
.capacity-selector__option.v-item--active {
  background: #f2edff;
}
.capacity-selector__option ::v-deep .v-input--selection-controls__input {
  width: 16px;
  height: 16px;
  margin-right: 12px;
}
.capacity-selector__option ::v-deep .v-input--selection-controls__ripple {
  display: none;
}
.capacity-selector__option ::v-deep .v-icon.v-icon {
  width: 16px;
  height: 16px;
  font-size: 16px;
}
.capacity-selector__option ::v-deep .mdi-radiobox-blank::before {
  content: url("@components/assets/uncheckedRadioIcon.svg");
}
.capacity-selector__option ::v-deep .mdi-radiobox-marked::before {
  content: url("@components/assets/checkedRadioIcons.svg");
}
.capacity-selector__option-content {
  display: flex;
  align-items: center;
  gap: 12px;
}
.capacity-selector__label {
  font-size: 14px;
  font-weight: 400;
  line-height: 16.94px;
  color: #101828;
}
</style>
